import * as Sentry from '@sentry/vue'
import type { StringObject, GenericObject, UnknownObject } from '~/types/common'
import type { UserRole } from '~/types/user'
import { useMonitoringContext } from './internal/context'
import { useSentry } from './useSentry'

interface UserJourney {
  addStep: (stepName: string, data?: UnknownObject) => Sentry.Span | undefined
  completeStep: (stepName: string, success?: boolean) => void
  complete: (success?: boolean) => void
  abandon: (reason: string) => void
}

interface FailedRequest {
  url: string
  method: string
  timestamp: number
  error?: string
}

interface NetworkError {
  message?: string
}

interface UseMonitoringReturn {
  logEvent: (message: string, level?: Sentry.SeverityLevel, extra?: StringObject) => void
  trackFeature: (featureName: string, properties?: GenericObject) => void
  trackBusinessMetric: (metricName: string, value: number, tags?: Record<string, string>) => void
  trackFeatureAdoption: (featureName: string, metadata?: StringObject) => void
  startUserJourney: (journeyName: string) => UserJourney
  trackSessionQuality: () => void
  trackAPISuccess: (method: string, status: number) => void
  trackAPIError: (method: string, status: number, errorMessage?: string) => void
  trackWebsocketEvent: (eventType: string, details?: StringObject) => void
  handleOfflineRequest: (url: string, method: string, error: NetworkError) => void
  monitorNetworkStatus: () => void
}

const getUserRoles = (): string => {
  const user = useAuthStore().user
  if (!user) return 'no-roles-found'
  return user.roles.map((role: UserRole): string => role.name).join(',')
}

const getUserTenure = (): string => {
  const user = useAuthStore().user
  if (!user?.created_at) return 'unknown'

  const days: number = Math.floor((Date.now() - new Date(user.created_at).getTime()) / (1000 * 60 * 60 * 24))

  if (days < 7) return 'new'
  if (days < 30) return 'recent'
  if (days < 90) return 'established'
  return 'veteran'
}

const getCurrentLicence = (): string => {
  const licence = useAuthStore().activeLicence
  return licence ? licence.name : 'no-licence'
}

const calculateSessionQuality = (errors: number, warnings: number, slowTransactions: number): number => {
  let score = 100
  score -= errors * 10
  score -= warnings * 2
  score -= slowTransactions * 5
  return Math.max(0, Math.min(100, score))
}

export const useMonitoring = (): UseMonitoringReturn => {
  const { addBreadcrumb } = useSentry()

  const logEvent = (message: string, level: Sentry.SeverityLevel = 'info', extra?: StringObject): void => {
    const { getBaseContext } = useMonitoringContext()
    const logData = {
      ...getBaseContext(),
      ...extra
    }

    switch (level) {
      case 'error':
      case 'fatal':
        Sentry.logger.error(message, logData)
        break
      case 'warning':
        Sentry.logger.warn(message, logData)
        break
      case 'info':
      case 'debug':
      case 'log':
      default:
        Sentry.logger.info(message, logData)
        break
    }
  }

  const trackFeature = (featureName: string, properties?: GenericObject): void => {
    const { getCrumbData } = useMonitoringContext()
    addBreadcrumb(`Used feature: ${featureName}`, 'feature', getCrumbData(properties) as StringObject)
  }

  const trackBusinessMetric = (metricName: string, value: number, tags?: Record<string, string>): void => {
    const span = Sentry.startInactiveSpan({
      op: 'metric',
      name: metricName,
      attributes: {
        'metric.name': metricName,
        'metric.value': value,
        ...tags
      }
    })

    if (span) {
      span.end()
    }

    addBreadcrumb(`Business metric: ${metricName}`, 'metric', {
      metric: metricName,
      value: value.toString(),
      ...tags
    })
  }

  const trackFeatureAdoption = (featureName: string, metadata?: StringObject): void => {
    Sentry.startSpan(
      {
        name: `feature.${featureName}`,
        op: 'feature.usage',
        attributes: {
          feature: featureName,
          'user.roles': getUserRoles(),
          'user.tenure': getUserTenure(),
          'user.licence': getCurrentLicence(),
          ...metadata
        }
      },
      (span: Sentry.Span): void => {
        span.end()
      }
    )
  }

  const startUserJourney = (journeyName: string): UserJourney => {
    const span: Sentry.Span | undefined = Sentry.startInactiveSpan({
      name: `journey.${journeyName}`,
      op: 'user.journey',
      attributes: {
        journey: journeyName,
        'start.time': new Date().toISOString()
      }
    })

    const steps = new Map<string, Sentry.Span>()

    return {
      addStep: (stepName: string, data?: Record<string, unknown>): Sentry.Span | undefined => {
        if (!span) return undefined

        const stepSpan: Sentry.Span | undefined = Sentry.startInactiveSpan({
          op: 'journey.step',
          name: stepName,
          attributes: {
            'step.name': stepName,
            ...data
          }
        })

        if (stepSpan) {
          steps.set(stepName, stepSpan)
        }

        return stepSpan
      },

      completeStep: (stepName: string, success = true): void => {
        const stepSpan: Sentry.Span | undefined = steps.get(stepName)
        if (stepSpan) {
          stepSpan.setStatus({
            code: success ? 1 : 2,
            message: success ? 'ok' : 'internal_error'
          })
          stepSpan.end()
        }
      },

      complete: (success = true): void => {
        if (span) {
          span.setStatus({
            code: success ? 1 : 2,
            message: success ? 'ok' : 'internal_error'
          })
          span.setAttribute('journey.completed', success)
          span.setAttribute('journey.steps', steps.size)
          span.end()
        }
      },

      abandon: (reason: string): void => {
        if (span) {
          span.setStatus({ code: 2, message: 'cancelled' })
          span.setAttribute('journey.abandoned', true)
          span.setAttribute('abandon.reason', reason)
          span.end()
        }

        Sentry.logger.info(`User journey abandoned: ${journeyName}`, {
          journey: journeyName,
          reason
        })
      }
    }
  }

  const trackWebsocketEvent = (eventType: string, details?: StringObject): void => {
    addBreadcrumb(`WebSocket event: ${eventType}`, 'websocket', {
      'ws.event': eventType,
      ...details
    })
  }

  const trackSessionQuality = (): void => {
    let errorCount = 0
    let warningCount = 0
    let slowTransactions = 0

    Sentry.addEventProcessor((event: Sentry.Event): Sentry.Event | null => {
      if (event.level === 'error') errorCount++
      if (event.level === 'warning') warningCount++

      if (
        event.type === 'transaction' &&
        event.contexts?.trace &&
        'data' in event.contexts.trace &&
        event.contexts.trace.data?.duration &&
        event.contexts.trace.data.duration > 3000
      ) {
        slowTransactions++
      }

      return event
    })

    window.addEventListener('beforeunload', (): void => {
      const sessionQuality: number = calculateSessionQuality(errorCount, warningCount, slowTransactions)

      trackBusinessMetric('session.quality', sessionQuality, {
        errors: errorCount.toString(),
        warnings: warningCount.toString(),
        slowTransactions: slowTransactions.toString()
      })
    })
  }

  const trackAPISuccess = (method: string, status: number): void => {
    addBreadcrumb(`API Success: ${method} ${status}`, 'api', {
      method,
      status: status.toString()
    })
  }

  const trackAPIError = (method: string, status: number, errorMessage?: string): void => {
    addBreadcrumb(`API Error: ${method} ${status}`, 'api', {
      method,
      status: status.toString(),
      ...(errorMessage && { error: errorMessage })
    })
  }

  const handleOfflineRequest = (url: string, method: string, error: NetworkError): void => {
    const failedRequestsJson: string = localStorage.getItem('failedRequests') || '[]'
    const failedRequests: FailedRequest[] = JSON.parse(failedRequestsJson)

    const newFailedRequest: FailedRequest = {
      url,
      method,
      timestamp: Date.now(),
      error: error?.message
    }

    failedRequests.push(newFailedRequest)
    localStorage.setItem('failedRequests', JSON.stringify(failedRequests))

    addBreadcrumb('Request failed offline - queued for retry', 'network', {
      url,
      method
    })
  }

  const monitorNetworkStatus = (): void => {
    window.addEventListener('online', () => {
      addBreadcrumb('Connection restored', 'network')

      const failedRequests: FailedRequest[] = JSON.parse(localStorage.getItem('failedRequests') || '[]')
      if (failedRequests.length > 0) {
        Sentry.logger.info('Retrying failed requests', { failedRequests })
        localStorage.removeItem('failedRequests')
      }
    })

    window.addEventListener('offline', () => {
      addBreadcrumb('Connection lost', 'network')
    })
  }

  return {
    logEvent,
    trackFeature,
    trackBusinessMetric,
    trackFeatureAdoption,
    startUserJourney,
    trackSessionQuality,
    trackAPISuccess,
    trackAPIError,
    trackWebsocketEvent,
    handleOfflineRequest,
    monitorNetworkStatus
  } as const
}
